import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const ImageUploadModal = ({ product, isOpen, onClose, onImagesUploaded }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const { toast } = useToast();

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      toast({
        title: "Warning",
        description: "Some files were skipped. Only image files are allowed.",
        variant: "destructive",
      });
    }
    
    setSelectedFiles(prev => [...prev, ...imageFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one image to upload",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    const uploadedImages = [];
    const newProgress = {};

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('is_main', 'false'); // Don't set as main by default

        try {
          const response = await axios.post(
            `${API}/products/${product.id}/images`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                newProgress[i] = percentCompleted;
                setProgress({ ...newProgress });
              }
            }
          );

          uploadedImages.push(response.data);
          newProgress[i] = 100;
          setProgress({ ...newProgress });
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          newProgress[i] = -1; // Error state
          setProgress({ ...newProgress });
        }
      }

      if (uploadedImages.length > 0) {
        onImagesUploaded(uploadedImages);
        toast({
          title: "Success",
          description: `${uploadedImages.length} image(s) uploaded successfully`,
        });
        onClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to upload any images",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setSelectedFiles([]);
      setProgress({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Upload Images
              </h3>
              <p className="text-sm text-slate-600">
                {product.title} - {product.images?.length || 0} existing images
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* File selection */}
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer flex flex-col items-center space-y-2"
            >
              <ImageIcon className="w-12 h-12 text-slate-400" />
              <div className="text-center">
                <p className="text-slate-900 font-medium">
                  Click to upload images
                </p>
                <p className="text-slate-500 text-sm">
                  or drag and drop multiple image files
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  PNG, JPG, GIF up to 10MB each
                </p>
              </div>
            </label>
          </div>

          {/* Selected files */}
          {selectedFiles.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Selected Files ({selectedFiles.length})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {uploading && (
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              progress[index] === -1 ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${Math.abs(progress[index] || 0)}%` }}
                          />
                        </div>
                      )}
                      {!uploading && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing images preview */}
          {product.images && product.images.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Existing Images ({product.images.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {product.images.slice(0, 6).map((image, index) => (
                  <div key={image.id} className="relative">
                    <img
                      src={`${BACKEND_URL}${image.image_url}`}
                      alt={image.image_name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    {image.is_main && (
                      <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 bg-yellow-500">
                        <span className="text-xs">★</span>
                      </Badge>
                    )}
                    {index === 5 && product.images.length > 6 && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          +{product.images.length - 6}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-200">
          <div className="text-sm text-slate-500">
            {selectedFiles.length > 0 && (
              `${selectedFiles.length} file(s) selected`
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Upload {selectedFiles.length} {selectedFiles.length === 1 ? 'Image' : 'Images'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;